'use client'

import {
  CreateSignatureSessionResponseBody,
  RetrieveSignatureSessionResponseBody,
  RetrieveSigningInfoResponseBody,
  SaveSigningFieldResponseResponseBody,
} from 'bff'
import React, { useCallback, useEffect, useState } from 'react'

interface AutosignSignerManifest {
  version: string
  released_at: string
  url: string
}

const getArchitecture = async (): Promise<'x64' | 'x32'> => {
  // Try modern API first
  if ('userAgentData' in navigator && (navigator as any).userAgentData) {
    try {
      const uaData = await (
        navigator as any
      ).userAgentData.getHighEntropyValues(['architecture', 'bitness'])
      if (uaData.bitness === '64' || uaData.architecture?.includes('64')) {
        return 'x64'
      }
    } catch (e) {
      // Fall through to legacy detection
    }
  }

  // Fallback to user agent parsing
  const userAgent = navigator.userAgent
  const platform = navigator.platform

  // Check for 64-bit indicators
  if (
    userAgent.includes('Win64') ||
    userAgent.includes('x64') ||
    userAgent.includes('x86_64') ||
    userAgent.includes('amd64') ||
    platform.includes('Win64') ||
    platform.includes('x64')
  ) {
    return 'x64'
  }

  // Default to 32-bit
  return 'x32'
}

export type SignatureBase64Cache = Record<string, string>

export interface SigningContext extends RetrieveSigningInfoResponseBody {
  autosignSignerManifest: AutosignSignerManifest
  token: string
  signerCompleted: boolean
  onSignerComplete: () => void
  remote: {
    saveFieldResponse: (
      fieldId: string,
      value: any
    ) => Promise<SaveSigningFieldResponseResponseBody>
    createSignatureSession: (
      fieldId: string
    ) => Promise<CreateSignatureSessionResponseBody>
    expireSignatureSession: (fieldId: string) => Promise<any>
    retrieveSignatureSession: (
      fieldId: string,
      sessionId: string
    ) => Promise<RetrieveSignatureSessionResponseBody>
  }
  userSignature: string | null
  signatureBase64Cache: SignatureBase64Cache
  setSignatureBase64Cache: React.Dispatch<
    React.SetStateAction<SignatureBase64Cache>
  >
}

const SigningContext = React.createContext<SigningContext | undefined>(
  undefined
)

export const useSigning = () => React.useContext(SigningContext)!

export const SigningProvider = ({
  children,
  data,
}: {
  children: React.ReactNode
  data: SigningContext
}) => {
  const [signerCompleted, setSignerCompleted] = useState(false)
  const [signatureBase64Cache, setSignatureBase64Cache] =
    useState<SignatureBase64Cache>({})

  const onSignerComplete = useCallback(() => setSignerCompleted(true), [])

  const [autosignSignerManifest, setAutosignSignerManifest] =
    useState<AutosignSignerManifest | null>(null)

  useEffect(() => {
    if (data.hsm_enabled) {
      getArchitecture().then((arch) => {
        fetch(
          `${process.env.NEXT_PUBLIC_AUTOSIGN_SIGNER_CLOUDFRONT_URL}/installers/desktop/win/${arch}/manifest.json`
        )
          .then((res) => res.json())
          .then(setAutosignSignerManifest)
      })
    }
  }, [])

  return (
    <SigningContext.Provider
      value={{
        ...data,
        autosignSignerManifest: autosignSignerManifest!,
        onSignerComplete,
        signerCompleted,
        signatureBase64Cache,
        setSignatureBase64Cache,
      }}
    >
      {children}
    </SigningContext.Provider>
  )
}
